import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Wrapper,
  Popup,
  BackgroundOverlay,
  PopupHeader,
  Reset,
  TitleSection,
  Title,
  FilterBodyContainer,
  Partition,
  CheckboxContainer,
  CheckboxLabel,
  AdvancedSearch,
  FilterFooterContainer,
  StyledButton,
  FilterTitle,
  QuickFiltersContainer,
  AdvanceFilterContainer,
} from "./index.styled";
import { ReactComponent as TripleLineOptions } from "../../Assets/Icons/TripleLineOptions.svg";
import { ReactComponent as CloseIcon } from "../../Assets/Icons/CrossSolid.svg";
import CustomInput from "../Input";
import {
  convertToMillion,
  formatHospitalDetails,
  isValidUSZipCode,
  viewSizeCalculator,
} from "../../utils";
import { useQueryClient } from "@tanstack/react-query";
import { getHospitalsByUrl } from "../../services/apiService";
import { generateTableTitle } from "../../utils/generateTableTitle";
import AdvanceFilterComponent from "../AdvanceFilterComp/AdvanceFilterComp";
import { useDispatch, useSelector } from "react-redux";
import {
  setHospitalsDetails,
  setTableResetting,
  clearProviderNumbers,
  setIsBookmarkTable,
} from "../../Redux/app/app.actions";
import {
  resetSelectedState,
  resetSpecificStateData,
} from "../../Redux/hospital/hospital.actions";
import { useFilters } from "../../hooks/useFilters";
import {
  cbsaList,
  countiesList,
  stateList,
  suggetionHospitalList,
  zipCodeList,
  facilityTypes,
  ownershipTypes,
  urban_rural_flag,
} from "../../Assets/MockData";
import { Loader } from "../../App";
import { states, views } from "../../Configs/config";
import { debounce } from "lodash";
import { Badge } from "../Header/index.styled";
import Info from "../Info";
import { useSearchParams } from "react-router-dom";
import CustomSlider from "../CustomSlider";

const fieldMapping = {
  hospital_name: "hospitalNames",
  zip_code: "zipCode",
  cbsa: "cbsa",
  state: "state",
  county: "county",
  fac_type: "facilityTypes",
  prov_control_type: "ownershipTypes",
  urban_rural_flag: "urban_rural_flag",
};

// Empty advanced-filter form shape. Used to seed the form when restoring
// applied filters from the URL on a page refresh, so every input has a defined
// value and the coc_accredited checkbox keeps its { checked, value } shape.
const EMPTY_FILTER_FORM = {
  hospital_name: "",
  cbsa: "",
  state: "",
  zip_code: "",
  county: "",
  CALC_STATS_BEDS_TOTAL: "",
  case_volume: "",
  facility_type: "",
  prov_control_type: "",
  coc_accredited: { checked: false, value: "" },
  urban_rural_flag: "",
  ownership_type: "",
  bed_count_min: "",
  bed_count_max: "",
  square_footage_min: "",
  square_footage_max: "",
  patient_discharge_min: "",
  patient_discharge_max: "",
  net_income_margin_min: "",
  net_income_margin_max: "",
  net_patient_revenue_min: "",
  net_patient_revenue_max: "",
  hospital_flags: "",
};

const FilterComp = ({
  filteredData,
  setFilteredData,
  customFilterFlag,
  setCustomFilterFlag,
  isPopupVisible,
  setIsPopupVisible,
  isAdvancedFilterVisible,
  setIsAdvancedFilterVisible,
  isSaveAndApply,
  setIsSaveAndApply,
  editSavedSearch,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [showloader, setShowLoader] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [isReset, setIsReset] = useState(false);
  const [suggestionsList, setSuggestionsList] = useState({
    hospitalNames: suggetionHospitalList,
    cbsa: cbsaList,
    zipCode: zipCodeList,
    state: stateList,
    county: countiesList,
    fac_type: facilityTypes,
    prov_control_type: ownershipTypes,
    urban_rural_flag: urban_rural_flag,
  });
  const [suggestionsLoader, setSuggestionsLoader] = useState({
    hospital_name: false,
    cbsa: false,
    zip_code: false,
    state: false,
    county: false,
    fac_type: false,
    prov_control_type: false,
    urban_rural_flag: false,
  });
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFetching, setIsFetching] = useState(false);
  const [lastActiveInputName, setLastActiveInputName] = useState(null);
  // Guards the one-time "restore advanced filters from the URL on refresh" flow.
  const didHydrateFromUrlRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { tableViewData, filterFlag } = useSelector((store) => store.app);
  const {
    partnerExclusiveFlag,
    globalFilter,
    quickFilter,
    states: selectedState,
    appliedFiltersCount,
    filters: appliedAdvancedFilters,
    setAdvancedFilters,
    resetFilters,
    view,
  } = useFilters();

  useEffect(() => {
    if (selectedState && selectedState.length === 1) {
      setFilteredData((prev) => ({
        ...prev,
        state: selectedState[0],
      }));
      handleInputChange("state", selectedState[0]);
    } else {
      setFilteredData((prev) => ({
        ...prev,
        state: "",
      }));
      handleInputChange("state", "");
    }
  }, [selectedState]);

  const handleFilters = (initialData) => {
    const filters = Object.fromEntries(
      Object.entries(initialData).filter(([key, value]) => {
        if (key === "coc_accredited") return value === true; // Add only if true
        return value !== undefined && value !== null && value !== ""; // Include other keys only if they have values
      }),
    );

    if (initialData?.coc_accredited?.checked === true) {
      filters.coc_accredited = initialData.coc_accredited.value;
    }
    // Persist the applied advanced filters to the URL (single source of truth)
    // AND switch to table view in the SAME navigation, so the view write can't
    // clobber the filter params (RR v7 stale-snapshot behavior).
    setAdvancedFilters(filters, views.TABLE);
  };

  const fetchFilteredHospitalsData = async (
    initialData = { ...filteredData },
    showTableView = true,
    ignoredField = null,
    signal,
  ) => {
    setIsFetching(true);
    if (isSaveAndApply) {
      const storedHistory = localStorage.getItem("searchHistory");
      const parsedHistory = JSON.parse(storedHistory);
      const updatedHistory = [
        ...parsedHistory.map((item) =>
          item.id === editSavedSearch.value.id
            ? { ...item, filteredData: { ...filteredData } }
            : item,
        ),
      ];
      localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
    }
    if (showTableView) {
      setShowLoader(true);
    }
    setSuggestionsLoader((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((key) => [key, !initialData[key]]),
      ),
    );
    const filtersTitle = `Search Results : ${generateTableTitle(initialData)}`;
    const url = constructApiUrl(initialData);

    try {
      // Route through the React Query cache; the constructed URL encodes the
      // full advanced-filter set, so identical filters are served from cache.
      const apiData = await queryClient.fetchQuery({
        queryKey: ["hospitals", url],
        queryFn: ({ signal: qSignal }) => getHospitalsByUrl(url, qSignal),
        staleTime: 5 * 60 * 1000,
      });
      // A superseded suggestion request: bail like the old aborted fetch did so
      // stale results don't overwrite the newer ones.
      if (signal?.aborted) {
        const abortError = new Error("Aborted");
        abortError.name = "AbortError";
        throw abortError;
      }
      const data = apiData.data || apiData; // Handle both old and new response formats
      const totalCount = apiData.total_count || 0; // Get total count from API
      
      if (showTableView) {
        // handleFilters writes the advanced filters AND view=table in one URL
        // navigation (see setAdvancedFilters), so no separate view write here.
        handleFilters(initialData);
        // Remove the problematic filter that was filtering out all data
        // const filteredData = data.filter((item) => item.name !== null);
        const hospitalDetailsArray = formatHospitalDetails(data);
        // Applying advanced filters over an active search: drop the search's
        // provider numbers so the quick-filter card counts (HomePage) recompute
        // from the applied filters instead of the stale search result set.
        dispatch(clearProviderNumbers());
        dispatch(
          setHospitalsDetails({
            view: views.TABLE,
            filterFlag: true,
            data: hospitalDetailsArray,
            tableTitle: isReset ? "" : filtersTitle,
            totalCount: totalCount, // Store total count from API
            // Applying advanced filters over an open bookmark table must drop the
            // bookmark context — otherwise LeftSection's fetch keeps taking the
            // bookmark branch (and the title stays "All Bookmarks").
            isBookmarkData: false,
          }),
        );
        dispatch(setIsBookmarkTable(false));

        setShowLoader(false);
        setIsReset(false);
      } else {
        if (data.length) {
          let updatedFilterData = { ...initialData };
          const uniqueStates = [...new Set(data.map((item) => item.state))];
          const uniqueCBSAs = [...new Set(data.map((item) => item.cbsa))];
          const uniqueZipCodes = [...new Set(data.map((item) => item.zip_code)),];
          const uniqueFacilityTypes = [...new Set(data.map((item) => item.fac_type)),];
              const uniqueOwnershipTypes = [...new Set(data.map((item) => item.prov_control_type)),];
    const ubiqueUrbanRuralFlag = [...new Set(data.map((item) => item.urban_rural_flag)),];

          if (initialData.zip_code.length > 0) {
            updatedFilterData["state"] =
              uniqueStates.length === 1 ? uniqueStates[0] : "";
          }
          if (initialData.cbsa.length > 0) {
            updatedFilterData["state"] =
              uniqueStates.length === 1 ? uniqueStates[0] : "";
            updatedFilterData["zip_code"] =
              uniqueZipCodes.length === 1 ? uniqueZipCodes[0] : "";
          }
          if (initialData.hospital_name.length > 0) {
            updatedFilterData["state"] =
              uniqueStates.length === 1 ? uniqueStates[0] : "";
            updatedFilterData["zip_code"] =
              uniqueZipCodes.length === 1 ? uniqueZipCodes[0] : "";
            updatedFilterData["cbsa"] =
              uniqueCBSAs.length === 1 ? uniqueCBSAs[0] : "";
            updatedFilterData["facility_type"] =
              uniqueFacilityTypes.length === 1 ? uniqueFacilityTypes[0] : "";
            updatedFilterData["prov_control_type"] =
              uniqueOwnershipTypes.length === 1 ? uniqueOwnershipTypes[0] : "";
            updatedFilterData["urban_rural_flag"] =
              ubiqueUrbanRuralFlag.length === 1 ? ubiqueUrbanRuralFlag[0] : "";
          }

          setFilteredData(updatedFilterData);
          setSuggestionsList((prevSuggestions) => ({
            ...prevSuggestions,
            ...extractUniqueSuggestions(data, prevSuggestions, ignoredField),
          }));
        }

        // autoFillStateWithSuggestions(extractUniqueSuggestions);
      }
    } catch (error) {
      // Clear the loader on failure so the UI never appears frozen when a
      // request is aborted, errors out, or the session is being refreshed.
      if (error.name !== "AbortError") {
        console.error("Error fetching filtered data:", error);
      }
      if (showTableView) {
        setShowLoader(false);
        setIsReset(false);
      }
    }
    if (showTableView) {
      setIsPopupVisible(false);
      // The applied-filter count is now derived from the URL params, so no
      // dispatch is needed here.
    }
    setSuggestionsLoader((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, false])),
    );
    // Note: applying advanced filters (handleFilters -> setAdvancedFilters)
    // already clears the map/legend selection (q + legendFilter) from the URL.
    setCustomFilterFlag(false);
    setIsFetching(false);
  };

  useEffect(() => {
    if (customFilterFlag) {
      fetchFilteredHospitalsData();
      setCustomFilterFlag(false);
    }
  }, [customFilterFlag]);

  // Refresh restoration: advanced filters live in the URL (the single source of
  // truth), but the form state + table data reset on reload. If the URL carries
  // advanced filters that haven't been applied yet, rebuild the form from those
  // params and fetch the filtered data once. LeftSection defers its own
  // all-hospitals fetch while this is pending (see hasUrlAdvancedFilters there).
  useEffect(() => {
    if (didHydrateFromUrlRef.current) return;
    const advancedKeys = Object.keys(appliedAdvancedFilters);
    if (advancedKeys.length === 0) return;
    didHydrateFromUrlRef.current = true;

    const hydrated = { ...EMPTY_FILTER_FORM, ...appliedAdvancedFilters };
    if (appliedAdvancedFilters.coc_accredited !== undefined) {
      hydrated.coc_accredited = {
        checked: true,
        value: appliedAdvancedFilters.coc_accredited,
      };
    }
    // A selected state is a reserved param, restored separately via the
    // selectedState effect, but seed it here too for the title/API URL.
    if (selectedState?.length === 1) hydrated.state = selectedState[0];

    // Always repopulate the form inputs from the URL. This covers BOTH a page
    // refresh and a back-navigation from the hospital factsheet (where the redux
    // store persists but this component's form state is fresh).
    setFilteredData(hydrated);

    // Only (re)fetch when the table data isn't already in redux. On a
    // back-navigation the store persisted the filtered rows, so refetching would
    // be wasteful; on a true refresh the store is empty and we must load it.
    const alreadyLoaded =
      tableViewData?.filterFlag && (tableViewData?.data?.length ?? 0) > 0;
    if (!alreadyLoaded) {
      fetchFilteredHospitalsData(hydrated, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const constructApiUrl = (initialData) => {
    // Only run the million conversion when at least one revenue field actually
    // holds a value (an empty/undefined field would otherwise turn into NaN).
    const hasRevenueValue = (v) =>
      v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));
    if (
      hasRevenueValue(initialData?.net_patient_revenue_min) ||
      hasRevenueValue(initialData?.net_patient_revenue_max)
    ) {
      initialData = convertToMillion(initialData);
    }

    // Parameter mapping from frontend to backend
    const parameterMapping = {
      facility_type: "fac_type",
      bed_count_min: "bed_count_min",
      bed_count_max: "bed_count_max",
      square_footage_min: "square_footage_min",
      square_footage_max: "square_footage_max",
      patient_discharge_min: "patient_discharge_min",
      patient_discharge_max: "patient_discharge_max",
      // Keep other parameters as is
      hospital_name: "hospital_name",
      cbsa: "cbsa",
      county: "county",
      zip_code: "zip_code",
      coc_accredited: "coc_accredited",
      urban_rural_flag: "urban_rural_flag",
      fac_type: "fac_type",
      prov_control_type: "prov_control_type",
      net_patient_revenue_min: "net_patient_revenue_min",
      net_patient_revenue_max: "net_patient_revenue_max",
    };

    const params = new URLSearchParams();
    
    if (initialData?.state) {
      if (Array.isArray(initialData.state)) {
        params.append("states", initialData.state.join(','));
      } 
      else if (typeof initialData.state === 'string') {
        params.append("states", initialData.state);
      }
      else if (typeof initialData.state === 'object' && initialData.state.value) {
        params.append("states", initialData.state.value);
      }
    }

    Object.entries(initialData).forEach(([key, value]) => {
      if (key === 'state') return;

      // Skip empty/falsy values AND numeric NaN so only real filters are sent.
      const isNaNValue = typeof value === "number" && Number.isNaN(value);
      if (value !== undefined && value !== "" && value !== null && !isNaNValue) {
        if (typeof value === "object" && "value" in value) {
          if (value.checked) {
            const backendKey = parameterMapping[key] || key;
            params.append(backendKey, value.value);
          }
        } else {
          const backendKey = parameterMapping[key] || key;
          params.append(backendKey, value);
        }
      }
    });

    params.append("partnerExclusiveArea", partnerExclusiveFlag);
    params.append("globalFilter", globalFilter?.value);
    // Quick filter lives in the URL alongside advanced filters; include it so a
    // refresh (which rebuilds this URL from params) keeps the quick filter
    // applied instead of fetching unfiltered-by-quickFilter results.
    if (quickFilter) {
      params.append("quickFilter", quickFilter);
    }

    return `${baseUrl}/new_data/hospitals/filter?${params.toString()}`;
};

  const extractUniqueSuggestions = (data, _prevSuggestions, ignoredField) => {
    if (!data) return {};
    const getUniqueValues = (array) => [...new Set(array)];

    const newSuggestions = {
      hospitalNames: data.map((item) => item.hosp10_name),
      zipCode: getUniqueValues(data.map((item) => item.zip_code?.toString())),
      cbsa: getUniqueValues(data.map((item) => item.cbsa?.toString())),
      state: getUniqueValues(data.map((item) => item.state)),
      county: getUniqueValues(data.map((item) => item.county)),
      fac_type: getUniqueValues(data.map((item) => item.fac_type)),
      prov_control_type: getUniqueValues(data.map((item) => item.prov_control_type)),
      urban_rural_flag: getUniqueValues(data.map((item) => item.urban_rural_flag)),
    };

    // For the edited fixed-domain dropdown, preserve its *previously computed*
    // availability instead of recomputing it (which collapses to the single
    // selected value) or resetting it to the full static list (which would
    // wrongly re-enable every option). This keeps the enabled/disabled options
    // tied to the active location filter even after the user picks a value in
    // that same dropdown.
    const preserveOwnAvailability = {
      facility_type: "fac_type",
      fac_type: "fac_type",
      prov_control_type: "prov_control_type",
      // Location combo fields: keep the edited field's own option list intact so
      // selecting a value doesn't collapse its dropdown to just that value (the
      // re-fetch filters by the picked value and would otherwise return only it).
      hospital_name: "hospitalNames",
      cbsa: "cbsa",
      zip_code: "zipCode",
      county: "county",
    };
    if (ignoredField && preserveOwnAvailability[ignoredField]) {
      const key = preserveOwnAvailability[ignoredField];
      const prevList = _prevSuggestions?.[key];
      if (Array.isArray(prevList) && prevList.length > 0) {
        newSuggestions[key] = [...prevList];
      }
    }

    // The State dropdown always renders the full list and has no
    // enabled/disabled restriction, so keep its option list complete.
    if (ignoredField === "state") {
      newSuggestions.state = [...stateList];
    }

    return newSuggestions;
  };

  const autoFillStateWithSuggestions = (extractedSuggestions) => {
    Object.entries(fieldMapping).forEach(([filteredKey, suggestionKey]) => {
      if (extractedSuggestions[suggestionKey]?.length === 1) {
        setFilteredData((prev) => ({
          ...prev,
          [filteredKey]: extractedSuggestions[suggestionKey][0],
        }));
      }
    });
  };

  const handleFilterClick = () => {
    if (isAdvancedFilterVisible) {
      setIsAdvancedFilterVisible((prev) => !prev);
    }
    setIsPopupVisible((prev) => !prev);

    setIsSaveAndApply(false);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);

    if (isAdvancedFilterVisible) {
      //setIsAdvancedFilterVisible(false);
    }
  };

  const handleInputChange = (name, value, isFacility = false) => {
    if (abortController) {
      abortController.abort();
    }

    let updatedFilterData = { ...filteredData };

    if (value !== null && value !== undefined && !Number.isNaN(value)) {
      updatedFilterData[name] = value;
    } else {
      delete updatedFilterData[name];
    }

    setFilteredData(updatedFilterData);

    // **Check if all fields are empty**
    const isAllFieldsEmpty = Object.values(updatedFilterData).every(
      (val) => !val || (typeof val === "object" && !val.value),
    );

    if (isAllFieldsEmpty) {
      updateSuggestionWithStaticData();
      debouncedFetch.cancel();
    } else {
      // if (name === "zip_code" && !isValidUSZipCode(value)) {
      //   debouncedFetch.cancel();
      //   return;
      // }
      const newAbortController = new AbortController();
      setAbortController(newAbortController);
      debouncedFetch(updatedFilterData, name, newAbortController.signal);
    }
  };

  const debouncedFetch = useCallback(
    debounce((data, ignoredField, signal) => {
      fetchFilteredHospitalsData(data, false, ignoredField, signal);
    }, 800),
    [],
  );

  const updateSuggestionWithStaticData = () => {
    setSuggestionsList({
      hospitalNames: [...suggetionHospitalList],
      cbsa: [...cbsaList],
      zipCode: [...zipCodeList],
      state: [...stateList],
      county: [...countiesList],
      fac_type: [...facilityTypes],
      prov_control_type: [...ownershipTypes],
      urban_rural_flag: [...urban_rural_flag],
    });
  };

  useEffect(() => {
    if (view === views.MAP) {
      // Abort any in-flight suggestion fetch and cancel the pending debounce
      // BEFORE clearing the form. A suggestion request (e.g. triggered by
      // selecting a hospital, which auto-fills state/zip/cbsa) otherwise
      // resolves after a global reset (logo click -> navigateToHome) and
      // repopulates the just-cleared inputs. handleReset already does this,
      // which is why the manual Reset works but the logo reset didn't.
      debouncedFetch.cancel();
      if (abortController) {
        abortController.abort();
        setAbortController(null);
      }
      resetToEmptyState();
      updateSuggestionWithStaticData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleCheckboxChange = (e) => {
    setFilteredData((prev) => ({
      ...prev,
      coc_accredited: {
        checked: e.target.checked,
        value: !e.target.checked ? "" : "commission on cancer (coc)",
      },
    }));
  };

  const resetToEmptyState = () => {
    setFilteredData((prevState) => ({
      ...prevState,
      hospital_name: "",
      cbsa: "",
      state: "",
      zip_code: "",
      county: "",
      CALC_STATS_BEDS_TOTAL: "",
      case_volume: "",
      facility_type: "",
      prov_control_type: "",
      coc_accredited: {
        checked: false,
        value: "",
      },
      urban_rural_flag: "",
      ownership_type: "",
      bed_count_min: "",
      bed_count_max: "",
      square_footage_min: "",
      square_footage_max: "",
      patient_discharge_min: "",
      patient_discharge_max: "",
      net_income_margin_min: "",
      net_income_margin_max: "",
      net_patient_revenue_min: "",
      net_patient_revenue_max: "",
      hospital_flags: "",
    }));
  };

  useEffect(() => {
    if (isReset && appliedFiltersCount > 0) {
      applyingFilters();
    }
    // NOTE: Do not reset appliedFiltersCount here. This effect runs on every
    // mount, so dispatching appliedFiltered(0) used to wipe the filter-count
    // badge whenever FilterComp remounted (e.g. returning from the factsheet),
    // even though the filters/table data were still applied.
  }, [isReset]);

  const handleReset = () => {
    // setIsReset(true);
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    // Always clear the filter form inputs and reset suggestions
    resetToEmptyState();
    updateSuggestionWithStaticData();

    // Only clear the table/applied-filter state when a filter is actually
    // applied to the current table. Typing values without applying them
    // should not wipe the existing table data.
    // Use tableViewData.filterFlag as the primary signal because it persists
    // across remounts (e.g. returning from the factsheet), unlike
    // appliedFiltersCount which can be transiently 0.
    const hasAppliedFilter =
      tableViewData?.filterFlag || filterFlag || appliedFiltersCount > 0;

    if (hasAppliedFilter) {
      // Keep the table skeleton visible during the reset -> re-fetch window so
      // the "No data" empty state doesn't flash before the all-hospitals data
      // arrives. Cleared once the re-fetch settles (see LeftSection).
      dispatch(setTableResetting(true));

      // Reset the table data and clear filters
      dispatch(
        setHospitalsDetails({
          view: views.TABLE,
          filterFlag: false,
          data: [],
          tableTitle: "",
          totalCount: 0,
          // Also drop the bookmark context so a reset from an open bookmark
          // table falls back to the all-hospitals list, not the bookmarks.
          isBookmarkData: false,
        }),
      );
      dispatch(setIsBookmarkTable(false));

      // Clear all filter params (advanced + states + legendFilter + q) in a
      // single navigation to avoid competing URL updates.
      resetFilters();

      // Reset state selection data - this was missing!
      dispatch(resetSelectedState());
      dispatch(resetSpecificStateData());
    }

    // Close the popup if in table view
    if (tableViewData?.view === views.TABLE) {
      setIsPopupVisible(false);
    }
  };

  const applyingFilters = () => {
    fetchFilteredHospitalsData();
  };

  const resetUrl = () => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete("state");
    currentParams.delete("legendFilter");
    setSearchParams(currentParams);
  };

  const isDisabled = (data) => {
    const isEmpty = Object.values(data).every((value) =>
      value === "" ||
      value === null ||
      value === undefined ||
      (typeof value === "object" && !value?.checked) ||
      (data?.zip_code && !isValidUSZipCode(data?.zip_code))
    );

    const isInvalidRange = (minKey, maxKey) => {
      const min = parseFloat(data[minKey]);
      const max = parseFloat(data[maxKey]);
      return !isNaN(min) && !isNaN(max) && min > max;
    };

    const invalidMinMax = [
      ["bed_count_min", "bed_count_max"],
      ["square_footage_min", "square_footage_max"],
      ["patient_discharge_min", "patient_discharge_max"],
      ["net_income_margin_min", "net_income_margin_max"],
      ["net_patient_revenue_min", "net_patient_revenue_max"],
    ].some(([minKey, maxKey]) => isInvalidRange(minKey, maxKey));

    return isEmpty || invalidMinMax;
  };

  const [noMatchFlags, setNoMatchFlags] = useState({
    state: false,
    zip_code: false,
    cbsa: false,
    hospital_name: false,
  });

  const handleNoMatch = (name, hasNoMatch) => {
    setNoMatchFlags((prevFlags) => ({
      ...prevFlags,
      [name]: hasNoMatch,
    }));
  };

  return (
    <>
      <BackgroundOverlay
        onClick={handleClosePopup}
        isPopupVisible={isPopupVisible}
        isAdvancedFilterVisible={isAdvancedFilterVisible}
      />
      <Info
        showArrow={false}
        config={{
          tooltipText: "Filters",
          position: "top",
          offsetY: 10,
        }}
      >
        <Wrapper onClick={handleFilterClick}>
          {appliedFiltersCount !== 0 && <Badge>{appliedFiltersCount}</Badge>}
          <TripleLineOptions />
        </Wrapper>
      </Info>
      <Popup
        isPopupVisible={isPopupVisible}
        isAdvancedFilterVisible={isAdvancedFilterVisible}
      >
        {isPopupVisible && !isAdvancedFilterVisible && (
          <QuickFiltersContainer>
            <PopupHeader>
              <TitleSection>
                <Info
                  showArrow={false}
                  config={{
                    tooltipText: 'Close',
                    position: 'top',
                    offsetY: 17,
                  }}
                  >
                    <CloseIcon
                      onClick={handleFilterClick}
                      className="close-icon"
                      style={{
                        cursor: "pointer",
                        fontSize: viewSizeCalculator(2),
                        width: viewSizeCalculator(2),
                        height: viewSizeCalculator(2),
                      }}
                    />
                  </Info>
                <Title>Quick Filters</Title>
              </TitleSection>
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Reset All Filters!",
                  position: "top",
                }}
              >
                <Reset
                  isDisabled={Object.values(filteredData).every(
                    (value) =>
                      value === "" ||
                      value === null ||
                      value === undefined ||
                      (typeof value === "object" && !value.checked),
                  )}
                  onClick={handleReset}
                >
                  Reset
                </Reset>
              </Info>
            </PopupHeader>
            <FilterBodyContainer>
              <Info
                showArrow={false}
                config={{
                  tooltipText: isFetching
                    ? "Bear with us we're fetching the data"
                    : !!filteredData.hospital_name
                      ? "Can't edit as a Hospital has already been selected"
                      : null,
                  position: "top",
                  offsetY: -10,
                }}
              >
                <CustomInput
                  placeholder={"Enter State"}
                  dropdown={true}
                  comboMode={true}
                  label={"State"}
                  name="state"
                  disabled={isFetching || !!filteredData.hospital_name}
                  value={
                    filteredData?.state
                      ? states[filteredData?.state] !== undefined
                        ? `${states[filteredData?.state]} (${filteredData?.state})`
                        : filteredData?.state
                      : ""
                  }
                  onChange={handleInputChange}
                  suggestions={stateList}
                  suggestionLoader={suggestionsLoader.state}
                  height={windowHeight > 720 && 2.2}
                  onNoMatch={handleNoMatch}
                  filteredData={filteredData}
                  lastActiveInputName={lastActiveInputName}
                  setLastActiveInputName={setLastActiveInputName}
                />
              </Info>
              <Info
                showArrow={false}
                config={{
                  tooltipText: isFetching
                    ? "Bear with us we're fetching the data"
                    : !!filteredData.hospital_name
                      ? "Can't edit as a Hospital has already been selected"
                      : null,
                  position: "top",
                  offsetY: -10,
                }}
              >
                <CustomInput
                  placeholder={"Enter ZIP Code"}
                  name="zip_code"
                  label={"ZIP Code"}
                  dropdown={filteredData?.state ? true : false}
                  parent={"State"}
                  disabled={isFetching || !!filteredData.hospital_name}
                  value={filteredData?.zip_code}
                  onChange={handleInputChange}
                  suggestions={suggestionsList.zipCode}
                  suggestionLoader={suggestionsLoader.zip_code}
                  error={
                    filteredData?.zip_code &&
                    !isValidUSZipCode(filteredData?.zip_code)
                  }
                  height={windowHeight > 720 && 2.2}
                  onNoMatch={handleNoMatch}
                  filteredData={filteredData}
                  lastActiveInputName={lastActiveInputName}
                  setLastActiveInputName={setLastActiveInputName}
                />
              </Info>
              <Info
                showArrow={false}
                config={{
                  tooltipText: isFetching
                    ? "Bear with us we're fetching the data"
                    : !!filteredData.hospital_name
                      ? "Can't edit as a Hospital has already been selected"
                      : null,
                  position: "top",
                  offsetY: -10,
                }}
              >
                <CustomInput
                  placeholder={"Enter CBSA"}
                  dropdown={filteredData?.state ? true : false}
                  label={"CBSA"}
                  name="cbsa"
                  parent={"ZIP Code"}
                  disabled={isFetching || !!filteredData.hospital_name}
                  value={filteredData.cbsa}
                  onChange={handleInputChange}
                  suggestions={suggestionsList.cbsa}
                  suggestionLoader={suggestionsLoader.cbsa}
                  height={windowHeight > 720 && 2.2}
                  onNoMatch={handleNoMatch}
                  filteredData={filteredData}
                  lastActiveInputName={lastActiveInputName}
                  setLastActiveInputName={setLastActiveInputName}
                />
              </Info>
              <CustomInput
                placeholder={"Enter Hospital or System Name"}
                name="hospital_name"
                label={"Hospital or System Name"}
                parent={"CBSA"}
                dropdown={filteredData?.state ? true : false}
                disabled={isFetching}
                // disabled={
                //   noMatchFlags.cbsa ||
                //   !suggestionsList.cbsa.length ||
                //   !filteredData?.state?.length ||
                //   !filteredData?.zip_code?.length ||
                //   !filteredData?.cbsa?.length
                // } // Disable if no related fields are filled
                value={filteredData.hospital_name}
                onChange={handleInputChange}
                suggestions={suggestionsList.hospitalNames}
                suggestionLoader={suggestionsLoader.hospital_name}
                height={windowHeight > 720 && 2.2}
                onNoMatch={handleNoMatch}
                filteredData={filteredData}
                lastActiveInputName={lastActiveInputName}
                setLastActiveInputName={setLastActiveInputName}
              />
              <CustomSlider
                title="Bed Count"
                onChange={handleInputChange}
                minValue={filteredData?.bed_count_min}
                maxValue={filteredData?.bed_count_max}
                showMinMaxFields={true}
                minValueName="bed_count_min"
                maxValueName="bed_count_max"
                filteredData={filteredData}
                step={1}
                disabled={isFetching || !!filteredData.hospital_name}
              />
              <Info
                showArrow={false}
                config={{
                  tooltipText: isFetching
                    ? "Bear with us we're fetching the data"
                    : !!filteredData.hospital_name
                      ? "Can't edit as a Hospital has already been selected"
                      : null,
                  position: "top",
                  offsetY: -10,
                }}
              >
                <CheckboxContainer>
                  <input
                    style={{
                      cursor:
                        isFetching || !!filteredData.hospital_name
                          ? "not-allowed"
                          : "pointer",
                      accentColor: "#F96250",
                      width: viewSizeCalculator(1.063),
                      height: viewSizeCalculator(1.063),
                    }}
                    type="checkbox"
                    id="coc_accredited"
                    checked={filteredData.coc_accredited?.checked}
                    onChange={handleCheckboxChange}
                    disabled={isFetching || !!filteredData.hospital_name}
                  />
                  <CheckboxLabel
                    htmlFor="coc_accredited"
                    disabled={isFetching || !!filteredData.hospital_name}
                  >
                    COC Accrediated
                  </CheckboxLabel>
                </CheckboxContainer>
              </Info>
              <Partition />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <AdvancedSearch
                  onClick={() => {
                    setIsAdvancedFilterVisible(true);
                  }}
                >
                  Advanced Search
                </AdvancedSearch>
              </div>
            </FilterBodyContainer>
            <FilterFooterContainer>
              <StyledButton onClick={handleClosePopup} isCancel={true}>
                Cancel
              </StyledButton>
              <StyledButton
                disabled={isDisabled(filteredData)}
                bgColor={"#333333"}
                onClick={applyingFilters}
              >
                {showloader ? (
                  <Loader
                    height={viewSizeCalculator(1.5)}
                    width={viewSizeCalculator(1.5)}
                  />
                ) : (
                  "Apply Filter"
                )}
              </StyledButton>
            </FilterFooterContainer>
          </QuickFiltersContainer>
        )}
        {isAdvancedFilterVisible && (
          <AdvanceFilterContainer>
            <PopupHeader>
              <TitleSection>
                <Title style={{ padding: "0 0.5rem" }}>Advanced Search</Title>
              </TitleSection>
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Reset All Filters!",
                  position: "top",
                }}
              >
                <Reset
                  isDisabled={Object.values(filteredData).every(
                    (value) =>
                      value === "" ||
                      value === null ||
                      value === undefined ||
                      (typeof value === "object" && !value.checked),
                  )}
                  onClick={handleReset}
                >
                  Reset
                </Reset>
              </Info>
            </PopupHeader>
            <FilterBodyContainer>
              <AdvanceFilterComponent
                isVisible={isAdvancedFilterVisible}
                setIsAdvancedFilterVisible={setIsAdvancedFilterVisible}
                filteredData={filteredData}
                setFilteredData={setFilteredData}
                handleInputChange={handleInputChange}
                fetchFilteredHospitalsData={fetchFilteredHospitalsData}
                showloader={showloader}
                suggestionsList={suggestionsList}
                suggestionsLoader={suggestionsLoader}
                handleReset={handleReset}
                resetUrl={resetUrl}
                isSaveAndApply={isSaveAndApply}
                editSavedSearch={editSavedSearch}
                onCancel={handleClosePopup}
                setIsSaveAndApply={setIsSaveAndApply}
                isFetching={isFetching}
              />
            </FilterBodyContainer>
          </AdvanceFilterContainer>
        )}
      </Popup>
    </>
  );
};

export default FilterComp;
